import React, { useState, useEffect, useRef, useCallback } from "react";
import useMedia from "../../hooks/useMedia";

if (!document.getElementById("cells-fonts")) {
  const l = document.createElement("link");
  l.id = "cells-fonts"; l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Raleway:wght@400;500;600;700&display=swap";
  document.head.appendChild(l);
}

if (!document.getElementById("cells-kf")) {
  const s = document.createElement("style");
  s.id = "cells-kf";
  s.textContent = `
    @keyframes cardEntrance {
      from { opacity: 0; transform: translateY(28px) scale(0.97); }
      to   { opacity: 1; transform: translateY(0)   scale(1);    }
    }
    .cells-card {
      transition: box-shadow 0.35s ease, border-color 0.35s ease,
                  transform 0.32s cubic-bezier(.22,1,.36,1);
    }
    .cells-card:not(.cells-card--open):hover {
      transform: translateY(-6px) scale(1.01);
      box-shadow: 0 28px 70px rgba(120,60,10,0.16) !important;
    }
    .cells-card--open {
      box-shadow: 0 28px 90px rgba(120,60,10,0.24) !important;
    }
    .cells-card-img {
      transition: transform 0.55s cubic-bezier(.22,1,.36,1);
    }
    .cells-card:not(.cells-card--open):hover .cells-card-img {
      transform: scale(1.07);
    }
    .cells-toggle-btn {
      display: flex; align-items: center; gap: 5px;
      font-family: 'Raleway', sans-serif; font-size: 11px;
      color: #d97706; font-weight: 700; letter-spacing: 1px; text-transform: uppercase;
      background: none; border: none; cursor: pointer; padding: 0;
      transition: color 0.2s, gap 0.2s;
    }
    .cells-toggle-btn:hover { color: #92400e; gap: 8px; }
    .cells-toggle-btn .t-arrow {
      display: inline-block; font-size: 16px; line-height: 1;
      transition: transform 0.35s cubic-bezier(.32,1,.28,1);
    }
    .cells-card--open .cells-toggle-btn .t-arrow { transform: rotate(90deg); }
    .cells-close-btn {
      background: #7c2d12; color: #fff; border: none;
      padding: 9px 22px; border-radius: 6px;
      font-family: 'Raleway', sans-serif; font-size: 10px;
      font-weight: 700; letter-spacing: 2px; text-transform: uppercase;
      cursor: pointer; transition: background 0.2s, transform 0.15s;
    }
    .cells-close-btn:hover { background: #2d6b5e; transform: scale(1.03); }
    .cat-btn { transition: all 0.2s ease; }
    .cat-btn:hover { transform: translateY(-1px); }
    @media (max-width: 640px) {
      .cells-row { grid-template-columns: 1fr !important; gap: 20px !important; }
    }
  `;
  document.head.appendChild(s);
}

const CATEGORIES = ["All", "Liturgy", "Prayer", "Formation", "Youth", "Community", "Service"];

const CAT_COLORS = {
  Liturgy: { bg: "rgba(124,45,18,0.09)", text: "#7c2d12", dot: "#7c2d12", border: "rgba(124,45,18,0.3)" },
  Prayer: { bg: "rgba(109,40,217,0.08)", text: "#6d28d9", dot: "#6d28d9", border: "rgba(109,40,217,0.3)" },
  Formation: { bg: "rgba(5,150,105,0.08)", text: "#059669", dot: "#059669", border: "rgba(5,150,105,0.3)" },
  Youth: { bg: "rgba(217,119,6,0.09)", text: "#b45309", dot: "#d97706", border: "rgba(217,119,6,0.3)" },
  Community: { bg: "rgba(37,99,235,0.08)", text: "#1d4ed8", dot: "#2563eb", border: "rgba(37,99,235,0.3)" },
  Service: { bg: "rgba(220,38,38,0.08)", text: "#b91c1c", dot: "#dc2626", border: "rgba(220,38,38,0.3)" },
};

const groups = [
  // --- CELLS & ASSOCIATIONS ---
  { name: "Confirmation Catechists", category: "Formation", desc: "Lay faithful who catechize and prepare candidates, often adolescents, to receive the Sacrament of Confirmation.", detail: "The Confirmation Catechists play a vital role in the spiritual journey of young parishioners. Through structured programs, retreats, and one-on-one mentoring, they walk alongside candidates as they discern and deepen their commitment to the Catholic faith.", priest: "Fr. Terence Murray", coordinator: "Belmira Horta Ribeiro", mobile: "9920013942", meetingDay: "2nd Sunday", time: "11 a.m.", venue: "Conference Room" },
  { name: "Family Cell", category: "Community", desc: "A small community group focused on fostering faith, fellowship, and mutual support among families within the parish.", detail: "Family Cells gather regularly to pray, share Scripture, and support one another in the joys and challenges of family life. They form the grassroots foundation of our parish community.", priest: "Fr. Terence Murray", coordinator: "Noel Dias", mobile: "9819935300", meetingDay: "2nd Saturday", time: "After 7 p.m. Mass", venue: "Conference Room" },
  { name: "Cultural & Sports Committee", category: "Community", desc: "Promotes and organizes cultural events and sports activities within the parish, encouraging wholesome recreation and fellowship.", detail: "The Cultural & Sports Committee brings the parish together through annual feasts, talent shows, sports days, and community celebrations.", priest: "Fr. Terence Murray", coordinator: "Gordon Fernandes", mobile: "9372470874", meetingDay: "N/A", time: "N/A", venue: "N/A" },
  { name: "Parish Youth Movement", category: "Youth", desc: "Engages young people in spiritual formation, service, and social activities, helping them grow in faith.", detail: "PYM is a vibrant movement that channels the energy and enthusiasm of young parishioners into meaningful service, prayer, and fellowship.", priest: "Fr. Terence Murray", coordinator: "Ryan D'souza", mobile: "9967875038", meetingDay: "3rd Sunday", time: "After 10 am Mass", venue: "Conference Room" },
  { name: "Prayer Group", category: "Prayer", desc: "Gathers regularly for communal prayer, intercession, Scripture reading, and reflection.", detail: "The Prayer Group meets weekly to intercede for the parish, the world, and one another. Their ministry of prayer is quiet but foundational.", priest: "Fr. Clarence Sequeira", coordinator: "Victor Chakravarthy", mobile: "9930406021", meetingDay: "2nd Friday", time: "8.30 p.m.", venue: "Basement Room" },
  { name: "Divine Mercy Group", category: "Prayer", desc: "Dedicated to promoting devotion to the Divine Mercy including praying the Chaplet.", detail: "Inspired by the revelations to St. Faustina, the Divine Mercy Group gathers to pray the Chaplet and reflect on the boundless mercy of God.", priest: "Fr. Clarence Sequeira", coordinator: "Robert Fernandes", mobile: "9820761022", meetingDay: "Daily Prayer", time: "3 p.m.", venue: "Ground Floor Chapel" },
  { name: "Extraordinary Ministers of Holy Communion", category: "Liturgy", desc: "Properly trained lay faithful who assist in distributing Holy Communion when needed.", detail: "These dedicated ministers assist at Mass and bring Holy Communion to the sick and homebound, ensuring that all parishioners can participate in the Eucharist.", priest: "Fr. Clarence Sequeira", coordinator: "Anjali Desouza", mobile: "9930553669", meetingDay: "4th Saturday", time: "8.00 p.m.", venue: "Church" },
  { name: "Konkani Sodality", category: "Community", desc: "A cultural and devotional association for the Konkani linguistic community.", detail: "The Konkani Sodality preserves and celebrates the rich cultural and spiritual heritage of the Konkani-speaking community within our parish.", priest: "Fr. Clarence Sequeira", coordinator: "Gretta Falliera", mobile: "9833698493", meetingDay: "Every Sunday", time: "After 7 a.m. Mass", venue: "Church" },
  { name: "Lectors / Cantors", category: "Liturgy", desc: "Lay faithful who proclaim the Word of God during liturgical celebrations.", detail: "Lectors and Cantors serve the liturgy by proclaiming Scripture and leading sung prayer. Their ministry requires preparation, prayerfulness, and a deep love for the Word of God.", priest: "Fr. Clarence Sequeira", coordinator: "Merlyn D'souza", mobile: "9833132414", meetingDay: "4th Saturday", time: "8.00 p.m.", venue: "Church" },
  { name: "Choir", category: "Liturgy", desc: "Leads and enhances the sung prayer of the assembly during liturgical celebrations.", detail: "The parish choir leads the congregation in worship through sacred music at Sunday Masses and special liturgies.", priest: "Fr. Clarence Sequeira", coordinator: "Maria D'mello", mobile: "9920374318", meetingDay: "4th Saturday", time: "8.00 p.m.", venue: "Church" },
  { name: "Hope and Life", category: "Service", desc: "Providing support and ministry to the bereaved, offering comfort and spiritual encouragement.", detail: "Hope and Life walks alongside those who have lost loved ones, offering grief support, memorial Masses, and a community of compassion.", priest: "Fr. Clarence Sequeira", coordinator: "Dipika Fernandes", mobile: "9321287399", meetingDay: "3 Programs", time: "Get-together-July 19", venue: "17 Nov: Session, 9-3-26 Pilgrimage" },
  { name: "Marian Movement", category: "Prayer", desc: "Devotional groups dedicated to honoring the Blessed Virgin Mary through prayer.", detail: "The Marian Movement gathers parishioners devoted to Our Lady, praying the Rosary, organizing Marian feast celebrations, and fostering a tender love for the Mother of God.", priest: "Fr. Clarence Sequeira", coordinator: "Dipika Fernandes", mobile: "9321287399", meetingDay: "Every Saturday", time: "6.00 p.m. Rosary", venue: "Ground Floor Chapel" },
  { name: "Sunday School", category: "Formation", desc: "Provides religious instruction and faith formation for children.", detail: "Sunday School teachers volunteer their time every week to form children in the Catholic faith through storytelling, art, prayer, and sacramental preparation.", priest: "Fr. Clarence Sequeira", coordinator: "Belmira Horta Ribeiro", mobile: "9920013942", meetingDay: "2nd Sunday", time: "10.30 a.m.", venue: "Conference Room" },
  { name: "Bombay Catholic Sabha", category: "Community", desc: "A prominent lay Catholic organization in Mumbai advocating for the Catholic community.", detail: "The Bombay Catholic Sabha represents and advocates for the Catholic community in Mumbai, engaging with civic issues, education, and the welfare of Catholics.", priest: "Fr. Tarsicius Fernandes", coordinator: "Luis Horta Ribeiro", mobile: "9820013942", meetingDay: "4th Sunday", time: "After 10 a.m. Mass", venue: "Basement Room" },
  { name: "St. Stephen's Migrant Association", category: "Service", desc: "Focused on supporting migrants and fostering belonging.", detail: "The Migrant Association welcomes those who have come from other parts of India, helping them find community, practical support, and a home in the parish.", priest: "Fr. Tarsicius Fernandes", coordinator: "Sibiriya Toppo", mobile: "9702123509", meetingDay: "4th Sunday", time: "After 4 p.m. Mass", venue: "Church Hall" },
  { name: "Altar Servers", category: "Youth", desc: "Young parishioners who assist the priest and deacon at the altar during Mass.", detail: "Altar Servers learn reverence, discipline, and service from an early age, helping out diligently during the Eucharistic celebrations.", priest: "Fr. Tarsicius Fernandes", coordinator: "-", mobile: "-", meetingDay: "Every Sunday", time: "After Sunday School", venue: "Church Hall" },
  { name: "Small Christian Communities", category: "Community", desc: "Groups of parishioners who meet regularly to share Scripture and prayer.", detail: "SCCs are the heartbeat of the parish — small groups that gather in neighbourhoods to read Scripture, pray, and support one another.", priest: "Fr. Tarsicius Fernandes", coordinator: "Yogita Parmar", mobile: "9869286296", meetingDay: "Twice a year- Sunday", time: "11-1p.m. Tentative", venue: "Church Hall" },
  { name: "Ushers", category: "Service", desc: "Lay volunteers who assist parishioners and ensure a welcoming environment.", detail: "Ushers are the first face of hospitality, welcoming all who enter, assisting with seating, and ensuring that every person feels at home.", priest: "Fr. Tarsicius Fernandes", coordinator: "Pankaj Parmar", mobile: "7977879092", meetingDay: "1st Saturday", time: "After 7 p.m. Mass", venue: "Church" },
];

function CategoryBadge({ category }) {
  const c = CAT_COLORS[category] || {};
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "4px 10px", borderRadius: 20,
      background: c.bg, color: c.text,
      border: `1px solid ${c.border}`,
      fontFamily: "'Raleway', sans-serif", fontSize: 9,
      fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase",
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
      {category}
    </span>
  );
}

function CellCard({ g, isOpen, onToggle, heroImage, cardRef, entranceDelay }) {
  return (
    <article
      ref={cardRef}
      className={`cells-card${isOpen ? " cells-card--open" : ""}`}
      onClick={onToggle}
      style={{
        borderRadius: 16, overflow: "hidden", background: "#fff",
        border: isOpen ? "1px solid rgba(217,119,6,0.4)" : "1px solid rgba(217,119,6,0.12)",
        boxShadow: "0 2px 4px rgba(0,0,0,0.03), 0 8px 32px rgba(120,60,10,0.07)",
        display: "flex", flexDirection: "column", cursor: "pointer", height: "100%",
        animation: `cardEntrance 0.55s ${entranceDelay}s cubic-bezier(.22,1,.36,1) both`,
      }}
    >
      {/* Image */}
      <div style={{
        height: isOpen ? 340 : 210, flexShrink: 0, overflow: "hidden",
        background: "linear-gradient(135deg, #fef3c7, #fde68a)", position: "relative",
        transition: "height 0.45s cubic-bezier(.32,1,.28,1)",
      }}>
        {heroImage ? (
          <img src={heroImage.image_url} alt={heroImage.caption || g.name}
            className="cells-card-img"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            loading="lazy" />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 44, opacity: 0.15, color: "#92400e" }}>✝</span>
          </div>
        )}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.06) 40%, transparent 70%)" }} />
        <div style={{ position: "absolute", top: 14, left: 14 }}>
          <CategoryBadge category={g.category} />
        </div>
        {isOpen && (
          <div style={{ position: "absolute", bottom: 16, left: 18, right: 18 }}>
            <p style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: 22, fontWeight: 700, color: "#fff",
              margin: 0, lineHeight: 1.2,
              textShadow: "0 2px 12px rgba(0,0,0,0.4)",
            }}>{g.name}</p>
          </div>
        )}
      </div>

      {/* Always-visible content */}
      <div style={{ padding: "20px 22px 16px", display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
        {!isOpen && (
          <h2 style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 21, fontWeight: 700, color: "#7c2d12", margin: 0, lineHeight: 1.2,
          }}>
            {g.name}
          </h2>
        )}
        <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: 13, color: "#78716c", lineHeight: 1.7, margin: 0 }}>
          {g.desc}
        </p>
        <div style={{ marginTop: "auto", paddingTop: 6, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button className="cells-toggle-btn" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
            <span>{isOpen ? "Show less" : "Learn more"}</span>
            <span className="t-arrow">›</span>
          </button>
          {!isOpen && (
            <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: 10, color: "#c4b5a0", letterSpacing: "0.5px" }}>
              tap to expand
            </span>
          )}
        </div>
      </div>

      <div style={{
        height: 1, margin: "0 22px",
        background: "linear-gradient(to right, rgba(217,119,6,0.2), transparent)",
        opacity: isOpen ? 1 : 0, transition: "opacity 0.3s ease",
      }} />

      {/* Expandable detail */}
      <div style={{ maxHeight: isOpen ? 850 : 0, overflow: "hidden", transition: "max-height 0.48s cubic-bezier(.32,1,.28,1)" }}>
        <div style={{
          padding: "18px 22px 24px",
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? "translateY(0)" : "translateY(10px)",
          transition: "opacity 0.32s ease 0.18s, transform 0.32s ease 0.18s",
        }}>

          {/* Ministry Info Grid using Excel Data */}
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px",
            margin: "0 0 20px", padding: "16px",
            background: "rgba(250,248,245,0.8)", borderRadius: 8,
            border: "1px solid rgba(120,60,10,0.08)",
            fontFamily: "'Raleway', sans-serif", fontSize: 12.5, color: "#57534e"
          }}>
            {g.priest && g.priest !== "N/A" && <div><strong style={{ color: "#7c2d12" }}>Priest In-Charge:</strong><br />{g.priest}</div>}
            {g.coordinator && g.coordinator !== "N/A" && g.coordinator !== "-" && <div><strong style={{ color: "#7c2d12" }}>Co-ordinator:</strong><br />{g.coordinator}</div>}
            {g.mobile && g.mobile !== "N/A" && g.mobile !== "-" && <div><strong style={{ color: "#7c2d12" }}>Mobile:</strong><br />{g.mobile}</div>}
            {g.meetingDay && g.meetingDay !== "N/A" && <div><strong style={{ color: "#7c2d12" }}>Meeting Day:</strong><br />{g.meetingDay}</div>}
            {g.time && g.time !== "N/A" && <div><strong style={{ color: "#7c2d12" }}>Time:</strong><br />{g.time}</div>}
            {g.venue && g.venue !== "N/A" && <div><strong style={{ color: "#7c2d12" }}>Venue:</strong><br />{g.venue}</div>}
          </div>

          <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: 13.5, color: "#4a4540", lineHeight: 1.85, margin: "0 0 20px" }}>
            {g.detail}
          </p>

          <button className="cells-close-btn" onClick={(e) => { e.stopPropagation(); onToggle(); }}>Close</button>
        </div>
      </div>

      <div style={{ height: 3, background: "linear-gradient(to right, #92400e, #d97706, #b45309)", flexShrink: 0, marginTop: "auto" }} />
    </article>
  );
}

export default function CellsAssociations() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [openIdx, setOpenIdx] = useState(null);
  const [filterKey, setFilterKey] = useState(0);
  const [prevCount, setPrevCount] = useState(groups.length);
  const [countAnim, setCountAnim] = useState(false);
  const cardRefs = useRef({});
  const media = useMedia();

  const filtered = groups.filter((g) => {
    const matchesSearch = (g.name + " " + g.desc).toLowerCase().includes(query.toLowerCase());
    const matchesCat = category === "All" || g.category === category;
    return matchesSearch && matchesCat;
  });

  useEffect(() => {
    if (filtered.length !== prevCount) {
      setCountAnim(true);
      setPrevCount(filtered.length);
      const t = setTimeout(() => setCountAnim(false), 400);
      return () => clearTimeout(t);
    }
  }, [filtered.length, prevCount]);

  useEffect(() => {
    setOpenIdx(null);
    setFilterKey(k => k + 1);
  }, [query, category]);

  const handleToggle = useCallback((gIdx) => {
    setOpenIdx(prev => {
      const next = prev === gIdx ? null : gIdx;
      if (next !== null) {
        setTimeout(() => {
          const el = cardRefs.current[gIdx];
          if (el) {
            const rect = el.getBoundingClientRect();
            const scrollTarget = window.scrollY + rect.top - (window.innerHeight / 2) + (rect.height / 2);
            window.scrollTo({ top: scrollTarget, behavior: "smooth" });
          }
        }, 80);
      }
      return next;
    });
  }, []);

  const rows = [];
  for (let i = 0; i < filtered.length; i += 2) rows.push(filtered.slice(i, i + 2));
  const getGlobalIdx = (name) => groups.findIndex(g => g.name === name);

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(160deg, #fefce8 0%, #fffbf0 35%, #fafaf9 100%)" }}>

      <header style={{ maxWidth: 1100, margin: "0 auto", padding: "64px 24px 40px", textAlign: "center" }}>
        <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: 10, letterSpacing: "5px", textTransform: "uppercase", color: "#d97706", margin: "0 0 12px", fontWeight: 700 }}>
          Saint Stephen's Church
        </p>
        <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "clamp(34px, 5.5vw, 60px)", fontWeight: 700, color: "#7c2d12", margin: "0 0 16px", lineHeight: 1.05, letterSpacing: "-0.5px" }}>
          Cells & Associations
        </h1>
        <div style={{ width: 56, height: 2, background: "linear-gradient(to right, #92400e, #d97706, transparent)", borderRadius: 2, margin: "0 auto 20px" }} />
        <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: 15, color: "#8a8075", maxWidth: 500, margin: "0 auto 24px", lineHeight: 1.75 }}>
          Discover the many ministries and community groups that help our parish grow in faith, service, and worship.
        </p>

        {/* Animated count */}
        <div style={{ marginBottom: 22 }}>
          <span style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 14, color: "#b8b0a8", letterSpacing: "0.5px", fontStyle: "italic",
            display: "inline-block",
            transition: "opacity 0.25s ease, transform 0.25s ease",
            opacity: countAnim ? 0.2 : 1,
            transform: countAnim ? "translateY(-5px)" : "translateY(0)",
          }}>
            {filtered.length} {filtered.length === 1 ? "group" : "groups"}
            {category !== "All" ? ` in ${category}` : " across all ministries"}
          </span>
        </div>

        {/* Search */}
        <div style={{ maxWidth: 420, margin: "0 auto 20px", position: "relative" }}>
          <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: "#d97706", pointerEvents: "none" }}>⌕</span>
          <input
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a group or ministry…"
            style={{
              width: "100%", padding: "13px 18px 13px 42px", borderRadius: 50,
              border: "1.5px solid rgba(217,119,6,0.22)", background: "rgba(255,255,255,0.9)",
              fontFamily: "'Raleway', sans-serif", fontSize: 13, color: "#44403c",
              outline: "none", boxShadow: "0 4px 24px rgba(120,60,10,0.07)",
              boxSizing: "border-box", transition: "border-color 0.2s, box-shadow 0.2s",
            }}
            onFocus={(e) => { e.target.style.borderColor = "#d97706"; e.target.style.boxShadow = "0 4px 24px rgba(217,119,6,0.16)"; }}
            onBlur={(e) => { e.target.style.borderColor = "rgba(217,119,6,0.22)"; e.target.style.boxShadow = "0 4px 24px rgba(120,60,10,0.07)"; }}
          />
        </div>

        {/* Category filters */}
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8 }}>
          {CATEGORIES.map(cat => {
            const active = category === cat;
            const c = CAT_COLORS[cat];
            return (
              <button key={cat} className="cat-btn" onClick={() => setCategory(cat)} style={{
                padding: "7px 18px", borderRadius: 20,
                border: active ? `1.5px solid ${c?.border || "rgba(217,119,6,0.4)"}` : "1.5px solid rgba(0,0,0,0.08)",
                background: active ? (c?.bg || "rgba(217,119,6,0.1)") : "rgba(255,255,255,0.8)",
                color: active ? (c?.text || "#d97706") : "#9a9490",
                fontFamily: "'Raleway', sans-serif", fontSize: 10, fontWeight: 700,
                letterSpacing: "1px", textTransform: "uppercase", cursor: "pointer",
                boxShadow: active ? `0 2px 12px ${c?.bg || "rgba(217,119,6,0.15)"}` : "none",
              }}>
                {cat}
              </button>
            );
          })}
        </div>
      </header>

      <main style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px 100px" }}>
        <div key={filterKey} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {rows.map((row, rowIdx) => {
            const gIdxA = getGlobalIdx(row[0].name);
            const gIdxB = row[1] ? getGlobalIdx(row[1].name) : null;
            const aOpen = openIdx === gIdxA;
            const bOpen = gIdxB !== null && openIdx === gIdxB;

            let cols = "1fr 1fr";
            if (aOpen) cols = "1fr 0fr";
            if (bOpen) cols = "0fr 1fr";

            return (
              <div key={`${filterKey}-${rowIdx}`} className="cells-row" style={{
                display: "grid",
                gridTemplateColumns: cols,
                gap: aOpen || bOpen ? 0 : 20,
                alignItems: "stretch",
                transition: "grid-template-columns 0.45s cubic-bezier(.32,1,.28,1), gap 0.45s ease",
              }}>
                <div style={{
                  overflow: "hidden", minWidth: 0,
                  opacity: bOpen ? 0 : 1,
                  transform: bOpen ? "scale(0.94)" : "scale(1)",
                  transition: "opacity 0.32s ease, transform 0.32s ease",
                }}>
                  <CellCard
                    g={row[0]} isOpen={aOpen}
                    heroImage={media.find(m => m.section === "cell" && m.subgroup === row[0].name)}
                    cardRef={el => cardRefs.current[gIdxA] = el}
                    onToggle={() => handleToggle(gIdxA)}
                    entranceDelay={Math.min(rowIdx * 0.07, 0.3)}
                  />
                </div>

                {row[1] && (
                  <div style={{
                    overflow: "hidden", minWidth: 0,
                    opacity: aOpen ? 0 : 1,
                    transform: aOpen ? "scale(0.94)" : "scale(1)",
                    transition: "opacity 0.32s ease, transform 0.32s ease",
                  }}>
                    <CellCard
                      g={row[1]} isOpen={bOpen}
                      heroImage={media.find(m => m.section === "cell" && m.subgroup === row[1].name)}
                      cardRef={el => cardRefs.current[gIdxB] = el}
                      onToggle={() => handleToggle(gIdxB)}
                      entranceDelay={Math.min(rowIdx * 0.07 + 0.04, 0.3)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div style={{
            textAlign: "center", padding: "80px 20px",
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 22, color: "#c4b5a0", fontStyle: "italic",
          }}>
            No groups found matching "{query}"{category !== "All" ? ` in ${category}` : ""}
          </div>
        )}
      </main>
    </div>
  );
}